import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage, auth } from './firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import './AdminLedger.css'; 
import './AdminMessages.css';

function AdminMessages() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  // Form States
  const [targetType, setTargetType] = useState('ALL'); // ALL, DEFAULTERS, PROPERTY, SPECIFIC
  const [propType, setPropType] = useState('houses');
  const [plotNo, setPlotNo] = useState('');
  
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [file, setFile] = useState(null);

  const fetchMessages = async () => {
    try {
      const q = query(collection(db, "resident_messages"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (targetType === 'SPECIFIC' && !plotNo.trim()) return alert("Please enter a Plot Number.");

    setLoading(true);
    try {
      let fileUrl = null;
      let fileType = null;

      // Handle File Upload if exists
      if (file) {
        const fileRef = ref(storage, `messages/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(snapshot.ref);
        fileType = file.type.includes('image') ? 'image' : 'document';
      }

      // Construct Target Data
      const targetData = { type: targetType };
      if (targetType === 'PROPERTY' || targetType === 'SPECIFIC') {
        targetData.propertyType = propType;
      }
      if (targetType === 'SPECIFIC') {
        targetData.plotNo = plotNo.trim();
      }

      const payload = {
        title: title.trim(),
        body: body.trim(),
        target: targetData,
        fileUrl: fileUrl,
        fileType: fileType,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, "resident_messages"), payload);
      
      // --- NEW: TRIGGER PUSH NOTIFICATIONS TO PHONES ---
      try {
        const idToken = await auth.currentUser.getIdToken();
        const flaskBaseUrl = "https://api.bathindahousingcomplex.com"; // Your backend URL
        
        await fetch(`${flaskBaseUrl}/send-admin-notification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            title: title.trim(),
            body: body.trim(),
            targetType: targetType,
            propertyType: (targetType === 'PROPERTY' || targetType === 'SPECIFIC') ? propType : null,
            plotNo: targetType === 'SPECIFIC' ? plotNo.trim() : null
          })
        });
      } catch (pushErr) {
        console.error("Failed to trigger phone beep, but message was saved:", pushErr);
      }
      // -------------------------------------------------

      alert("✅ Message Sent and Notifications Pushed Successfully!");
      
      // Reset Form
      setTitle(''); setBody(''); setFile(null); setPlotNo(''); setTargetType('ALL');
      fetchMessages();

    } catch (err) {
      alert("Error sending message: " + err.message);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this message? It will be removed from resident apps immediately.")) {
      await deleteDoc(doc(db, "resident_messages", id));
      fetchMessages();
    }
  };

  const getBadgeClass = (type) => {
    switch(type) {
      case 'ALL': return 'badge-all';
      case 'DEFAULTERS': return 'badge-defaulters';
      case 'PROPERTY': return 'badge-property';
      case 'SPECIFIC': return 'badge-specific';
      default: return 'badge-all';
    }
  };

  const formatTarget = (target) => {
    if (target.type === 'ALL') return "Everyone";
    if (target.type === 'DEFAULTERS') return "Pending Bill Users";
    if (target.type === 'PROPERTY') return `All ${target.propertyType}`;
    if (target.type === 'SPECIFIC') return `${target.propertyType} #${target.plotNo}`;
    return "Unknown";
  };

  return (
    <div className="messages-wrapper">
      
      <header className="ledger-header" style={{position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid #eee', padding: '15px 0'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div>
            <h2 style={{margin: 0, color: '#2c3e50'}}>✉️ Direct Messages & Alerts</h2>
            <p style={{margin: '5px 0 0 0', fontSize: '13px', color: '#7f8c8d'}}>Push notifications and messages to resident dashboards/apps.</p>
          </div>
          <button onClick={() => navigate('/admin')} style={{padding: '8px 16px', background: '#34495e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'}}>
            ← Back to Admin
          </button>
        </div>
      </header>

      <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 20px'}} className="messages-layout fade-in-up">
        
        {/* --- COMPOSER (LEFT) --- */}
        <div>
          <div className="form-card floating-card" style={{position: 'sticky', top: '100px'}}>
            <h3 style={{margin: '0 0 20px 0', color: '#e056fd', borderBottom: '2px solid #eee', paddingBottom: '10px'}}>
              ✏️ Compose Message
            </h3>
            
            <form onSubmit={handleSend}>
              
              {/* TARGET SELECTION */}
              <div className="target-box">
                <label style={{fontSize:'12px', fontWeight:'bold', color:'#333'}}>Send To Audience:</label>
                <select className="input-field" value={targetType} onChange={e => setTargetType(e.target.value)} style={{marginBottom: '10px', background: 'white'}}>
                  <option value="ALL">📢 All Residents in Colony</option>
                  <option value="DEFAULTERS">⚠️ Residents with Pending Bills</option>
                  <option value="PROPERTY">🏢 Specific Property Type</option>
                  <option value="SPECIFIC">👤 Single Specific Resident</option>
                </select>

                {(targetType === 'PROPERTY' || targetType === 'SPECIFIC') && (
                  <select className="input-field" value={propType} onChange={e => setPropType(e.target.value)} style={{marginBottom: targetType === 'SPECIFIC' ? '10px' : '0', background: 'white'}}>
                    <option value="houses">Houses</option>
                    <option value="shops">Shops</option>
                    <option value="booths">Booths</option>
                    <option value="c_buildings">Commercial Buildings</option>
                    <option value="n_home">Nursing Homes</option>
                  </select>
                )}

                {targetType === 'SPECIFIC' && (
                  <input type="text" className="input-field" placeholder="Enter Plot Number (e.g. 495)" value={plotNo} onChange={e => setPlotNo(e.target.value)} style={{marginBottom: 0, background: 'white'}} required />
                )}
              </div>

              {/* MESSAGE CONTENT */}
              <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Message Title</label>
              <input className="input-field" placeholder="e.g. Urgent: Water Supply Interruption" value={title} onChange={e => setTitle(e.target.value)} required />

              <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Detailed Message</label>
              <textarea className="input-field" rows="4" placeholder="Write your message here..." value={body} onChange={e => setBody(e.target.value)} required />

              <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Attach File / Image (Optional)</label>
              <input className="input-field" type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files[0])} />

              <button type="submit" disabled={loading} className="btn-primary-full" style={{background: 'linear-gradient(135deg, #e056fd, #eb4d4b)', marginTop: '10px'}}>
                {loading ? "Sending securely..." : "🚀 Send Message"}
              </button>
            </form>
          </div>
        </div>

        {/* --- HISTORY (RIGHT) --- */}
        <div>
          <h3 style={{color: '#2c3e50', marginTop: 0}}>Sent Messages History</h3>
          
          {messages.length === 0 ? (
            <div style={{padding: '40px', textAlign: 'center', background: 'white', borderRadius: '12px'}}>
              <p style={{color: '#888'}}>No messages sent yet.</p>
            </div>
          ) : (
            <div style={{maxHeight: '75vh', overflowY: 'auto', paddingRight: '10px'}}>
              {messages.map(msg => (
                <div key={msg.id} className="msg-history-card fade-in-up">
                  <div className="msg-header">
                    <div>
                      <h4 className="msg-title">{msg.title}</h4>
                      <div style={{fontSize: '11px', color: '#999', marginBottom: '8px'}}>
                        Sent: {msg.createdAt?.seconds ? new Date(msg.createdAt.seconds * 1000).toLocaleString() : 'Just now'}
                      </div>
                    </div>
                    <span className={`target-badge ${getBadgeClass(msg.target?.type)}`}>
                      {formatTarget(msg.target)}
                    </span>
                  </div>
                  
                  <div className="msg-body">{msg.body}</div>
                  
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px'}}>
                    {msg.fileUrl ? (
                      <a href={msg.fileUrl} target="_blank" rel="noreferrer" style={{fontSize: '12px', color: '#3498db', fontWeight: 'bold', textDecoration: 'none'}}>
                        📎 View Attachment
                      </a>
                    ) : <span />}
                    
                    <button onClick={() => handleDelete(msg.id)} style={{background: 'none', border: 'none', color: '#e74c3c', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold'}}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default AdminMessages;